import React, { useState, useEffect } from 'react';
import {
  BarChart3, TrendingUp, Users, Eye, MousePointer, Calendar, Download, RefreshCw,
  FileText, MapPin, Filter, ArrowLeft, Search, Globe, Smartphone, Monitor, Clock,
  Building2, Tablet, ExternalLink, Activity, Zap, PieChart, Target
} from 'lucide-react';
import { analyticsDb, appSettings } from '../lib/supabase';

// ==================== CONSTANTS ====================
const PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

// Page name mappings for better display
const PAGE_NAMES = {
  'home': 'Home Page',
  'offences': 'Offences & Fines',
  'walkthrough': 'Police Stop Guide',
  'lawyers': 'Lawyers Directory',
  'insurance': 'Insurance Companies',
  'evaluators': 'Vehicle Evaluators',
  'mechanics': 'Mechanics & Garages',
  'parts': 'Auto Parts Stores',
  'services': 'Service Providers',
  'traffic': 'Traffic Updates',
  'hotspots': 'Hotspots',
  'cameras': 'Speed Cameras',
  'documents': 'My Documents',
  'tickets': 'Ticket Tracker',
  'accident': 'Accident Report',
  'demerit': 'Demerit Points',
  'fees': 'Fees & Charges',
  'howto': 'How-To Guides',
  'learner': 'Learner Guide',
  'roadcode': 'Road Code',
  'resources': 'Resources',
  'getlisted': 'Get Listed',
  'login': 'Login/Register',
  'askbot': 'Ask Bot',
  'calculator': 'Fine Calculator',
  'settings': 'Settings'
};

// ==================== DATE RANGE HELPERS ====================
const getDateRange = (period) => {
  const end = new Date();
  const start = new Date();

  switch (period) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case 'week':
      start.setDate(end.getDate() - 7);
      break;
    case 'month':
      start.setMonth(end.getMonth() - 1);
      break;
    case 'quarter':
      start.setMonth(end.getMonth() - 3);
      break;
    case 'year':
      start.setFullYear(end.getFullYear() - 1);
      break;
    default:
      start.setDate(end.getDate() - 30);
  }

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
};

// ==================== SIMPLE BAR CHART COMPONENT ====================
const SimpleBarChart = ({ data, maxValue, color = 'indigo' }) => {
  if (!data || data.length === 0) return null;
  const max = maxValue || Math.max(...data.map(d => d.value));

  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="w-20 text-xs text-gray-600 truncate" title={item.label}>
            {item.label}
          </span>
          <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
            <div
              className={`h-full bg-${color}-500 rounded-full flex items-center justify-end pr-2`}
              style={{ width: `${max > 0 ? (item.value / max) * 100 : 0}%`, minWidth: item.value > 0 ? '40px' : '0' }}
            >
              <span className="text-xs text-white font-bold">{item.value}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==================== TREND CHART COMPONENT ====================
const TrendChart = ({ data, height = 120 }) => {
  if (!data || data.length === 0) return <p className="text-gray-500 text-center py-8">No trend data available</p>;

  const maxValue = Math.max(...data.map(d => d.page_views), 1);
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1 || 1)) * 100;
    const y = 100 - (d.page_views / maxValue) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="relative" style={{ height }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        {/* Grid lines */}
        <line x1="0" y1="25" x2="100" y2="25" stroke="#e5e7eb" strokeWidth="0.5" />
        <line x1="0" y1="50" x2="100" y2="50" stroke="#e5e7eb" strokeWidth="0.5" />
        <line x1="0" y1="75" x2="100" y2="75" stroke="#e5e7eb" strokeWidth="0.5" />

        {/* Area fill */}
        <polygon
          points={`0,100 ${points} 100,100`}
          fill="url(#gradient)"
          opacity="0.3"
        />

        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke="#6366f1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Gradient definition */}
        <defs>
          <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* X-axis labels */}
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
};

// ==================== DEVICE ICON COMPONENT ====================
const DeviceIcon = ({ device }) => {
  switch (device) {
    case 'mobile': return <Smartphone className="w-5 h-5" />;
    case 'tablet': return <Tablet className="w-5 h-5" />;
    default: return <Monitor className="w-5 h-5" />;
  }
};

// ==================== ADMIN ANALYTICS DASHBOARD ====================
export const AdminAnalyticsPage = ({ setPage }) => {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);

  // Data states
  const [summary, setSummary] = useState(null);
  const [topPages, setTopPages] = useState([]);
  const [topSearches, setTopSearches] = useState([]);
  const [topOffences, setTopOffences] = useState([]);
  const [topListings, setTopListings] = useState([]);
  const [deviceBreakdown, setDeviceBreakdown] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [adPerformance, setAdPerformance] = useState([]);
  const [topDirectoryListings, setTopDirectoryListings] = useState([]);
  const [listingsByType, setListingsByType] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    const { start, end } = getDateRange(period);

    try {
      const [
        summaryRes,
        pagesRes,
        searchesRes,
        offencesRes,
        listingsRes,
        devicesRes,
        referrersRes,
        trendRes,
        eventsRes,
        adPerfRes,
        topDirListingsRes,
        listingsByTypeRes
      ] = await Promise.all([
        analyticsDb.getSummary(start, end),
        analyticsDb.getTopPages(start, end, 10),
        analyticsDb.getTopSearches(start, end, 10),
        analyticsDb.getTopEntities(start, end, 'offence', 10),
        analyticsDb.getTopEntities(start, end, 'lawyer', 10),
        analyticsDb.getDeviceBreakdown(start, end),
        analyticsDb.getReferrers(start, end, 10),
        analyticsDb.getDailyTrend(start, end),
        analyticsDb.getRecentEvents(20),
        analyticsDb.getAdPerformance(start, end),
        analyticsDb.getTopDirectoryListings(start, end, 15),
        analyticsDb.getListingViewsByType(start, end)
      ]);

      setSummary(summaryRes.data?.[0] || {});
      setTopPages(pagesRes.data || []);
      setTopSearches(searchesRes.data || []);
      setTopOffences(offencesRes.data || []);
      setTopListings(listingsRes.data || []);
      setDeviceBreakdown(devicesRes.data || []);
      setReferrers(referrersRes.data || []);
      setDailyTrend(trendRes.data || []);
      setRecentEvents(eventsRes.data || []);
      setAdPerformance(adPerfRes.data || []);
      setTopDirectoryListings(topDirListingsRes.data || []);
      setListingsByType(listingsByTypeRes.data || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).map(v =>
      typeof v === 'object' ? JSON.stringify(v) : v
    ).join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatEventType = (type) => {
    const types = {
      'page_view': 'Page View',
      'search': 'Search',
      'directory_view': 'Directory View',
      'directory_click': 'Directory Click',
      'offence_view': 'Offence View',
      'ad_impression': 'Ad Impression',
      'ad_click': 'Ad Click'
    };
    return types[type] || type;
  };

  const getPageName = (page) => PAGE_NAMES[page] || page;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const totalDevices = deviceBreakdown.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <BarChart3 className="w-8 h-8" />Analytics Dashboard
            </h2>
            <p className="text-indigo-200 mt-1">Comprehensive insights into user engagement</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30">
              <Filter className="w-4 h-4" />Filters
            </button>
            <button onClick={loadAnalytics} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap gap-4">
            <div>
              <label className="block text-sm text-indigo-200 mb-1">Time Period</label>
              <select value={period} onChange={(e) => setPeriod(e.target.value)}
                className="px-4 py-2 rounded-lg bg-white/20 text-white border-0 cursor-pointer">
                <option value="today" className="text-gray-900">Today</option>
                <option value="week" className="text-gray-900">Last 7 Days</option>
                <option value="month" className="text-gray-900">Last 30 Days</option>
                <option value="quarter" className="text-gray-900">Last 3 Months</option>
                <option value="year" className="text-gray-900">Last Year</option>
              </select>
            </div>
            <div className="flex-1" />
            <button onClick={() => exportCSV(recentEvents, 'analytics_events')}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30">
              <Download className="w-4 h-4" />Export All Data
            </button>
          </div>
        )}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Events', value: summary?.total_events || 0, icon: Activity, color: 'blue', bg: 'bg-blue-500' },
          { label: 'Unique Sessions', value: summary?.unique_sessions || 0, icon: Users, color: 'green', bg: 'bg-green-500' },
          { label: 'Page Views', value: summary?.page_views || 0, icon: Eye, color: 'purple', bg: 'bg-purple-500' },
          { label: 'Searches', value: topSearches.reduce((sum, s) => sum + s.search_count, 0), icon: Search, color: 'orange', bg: 'bg-orange-500' }
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl p-5 shadow-lg">
            <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center mb-3`}>
              <stat.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-3xl font-black text-gray-900">{stat.value.toLocaleString()}</p>
            <p className="text-gray-500 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Trend Chart */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white p-4">
          <h3 className="font-bold flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />Page Views Over Time
          </h3>
        </div>
        <div className="p-6">
          <TrendChart data={dailyTrend} height={150} />
        </div>
      </div>

      {/* Top Pages & Top Searches */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 flex items-center justify-between">
            <h3 className="font-bold flex items-center gap-2">
              <Eye className="w-5 h-5" />Top Viewed Pages
            </h3>
            <button onClick={() => exportCSV(topPages, 'top_pages')} className="p-2 hover:bg-white/20 rounded-lg">
              <Download className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4">
            {topPages.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No page view data yet</p>
            ) : (
              <div className="space-y-3">
                {topPages.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-bold">
                        {i + 1}
                      </span>
                      <span className="font-medium">{getPageName(item.page)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-bold">{item.view_count.toLocaleString()}</span>
                      <span className="text-gray-400 text-sm">views</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top Searches */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 flex items-center justify-between">
            <h3 className="font-bold flex items-center gap-2">
              <Search className="w-5 h-5" />Top Search Terms
            </h3>
            <button onClick={() => exportCSV(topSearches, 'top_searches')} className="p-2 hover:bg-white/20 rounded-lg">
              <Download className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4">
            {topSearches.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No search data yet</p>
            ) : (
              <div className="space-y-3">
                {topSearches.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center text-sm font-bold">
                        {i + 1}
                      </span>
                      <span className="font-medium">"{item.query}"</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-orange-600 font-bold">{item.search_count.toLocaleString()}</span>
                      <span className="text-gray-400 text-sm">searches</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Device & Referrer Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Device Breakdown */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white p-4">
            <h3 className="font-bold flex items-center gap-2">
              <Smartphone className="w-5 h-5" />Device Breakdown
            </h3>
          </div>
          <div className="p-6">
            {totalDevices === 0 ? (
              <p className="text-gray-500 text-center py-8">No device data yet</p>
            ) : (
              <div className="space-y-4">
                {deviceBreakdown.map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      item.device === 'mobile' ? 'bg-pink-100 text-pink-600' :
                      item.device === 'tablet' ? 'bg-purple-100 text-purple-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      <DeviceIcon device={item.device} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium capitalize">{item.device}</span>
                        <span className="text-gray-500">{item.count.toLocaleString()} ({item.percentage}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            item.device === 'mobile' ? 'bg-pink-500' :
                            item.device === 'tablet' ? 'bg-purple-500' :
                            'bg-blue-500'
                          }`}
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Traffic Sources */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-4">
            <h3 className="font-bold flex items-center gap-2">
              <Globe className="w-5 h-5" />Traffic Sources
            </h3>
          </div>
          <div className="p-4">
            {referrers.length === 0 ? (
              <div className="text-center py-8">
                <Globe className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No referrer data yet</p>
                <p className="text-xs text-gray-400 mt-1">Most visits are direct traffic</p>
              </div>
            ) : (
              <div className="space-y-3">
                {referrers.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{item.source}</span>
                    </div>
                    <span className="text-emerald-600 font-bold">{item.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Offences */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <FileText className="w-5 h-5" />Top Viewed Offences
          </h3>
          <button onClick={() => exportCSV(topOffences, 'top_offences')} className="p-2 hover:bg-white/20 rounded-lg">
            <Download className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 max-h-64 overflow-y-auto">
          {topOffences.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No offence view data yet</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {topOffences.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
                    <span className="font-medium text-sm truncate max-w-[200px]">{item.entity_name || item.entity_id}</span>
                  </div>
                  <span className="text-purple-600 font-bold">{item.view_count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Service Provider Listings Section */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <Building2 className="w-5 h-5" />Service Provider Listings Performance
          </h3>
          <button onClick={() => exportCSV(topDirectoryListings, 'top_service_providers')} className="p-2 hover:bg-white/20 rounded-lg">
            <Download className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          {/* Listing Views by Category */}
          {listingsByType.length > 0 && (
            <div className="mb-6">
              <h4 className="font-bold text-gray-700 mb-3">Views by Category</h4>
              <div className="flex flex-wrap gap-3">
                {listingsByType.map((item, i) => (
                  <div key={i} className="px-4 py-2 bg-gray-100 rounded-xl">
                    <span className="capitalize font-medium">{item.type}</span>
                    <span className="ml-2 text-teal-600 font-bold">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Individual Listings */}
          <h4 className="font-bold text-gray-700 mb-3">Top Individual Listings</h4>
          {topDirectoryListings.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No listing view data yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-500">#</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-500">Listing Name</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-500">Type</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-500">Views</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-500">Clicks</th>
                    <th className="px-3 py-2 text-right text-xs font-bold text-gray-500">CTR</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {topDirectoryListings.map((item, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm font-bold text-gray-400">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-sm">{item.entity_name || item.entity_id}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs capitalize">
                          {item.entity_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-gray-700">{item.views}</td>
                      <td className="px-3 py-2 text-right font-bold text-teal-600">{item.clicks}</td>
                      <td className="px-3 py-2 text-right text-sm text-gray-500">
                        {item.views > 0 ? ((item.clicks / item.views) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Ad Performance Section */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white p-4 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <Target className="w-5 h-5" />Advertisement Performance
          </h3>
          <button onClick={() => exportCSV(adPerformance, 'ad_performance')} className="p-2 hover:bg-white/20 rounded-lg">
            <Download className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          {adPerformance.length === 0 ? (
            <div className="text-center py-8">
              <Target className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No ads configured yet</p>
              <p className="text-xs text-gray-400 mt-1">Create banner ads in the Ads management section</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Ad Name</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Advertiser</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Placement</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">Impressions</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">Clicks</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">CTR</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {adPerformance.map((ad, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{ad.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{ad.advertiser_name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-gray-100 rounded-full text-xs capitalize">{ad.placement}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold">{(ad.impressions || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-bold text-pink-600">{(ad.clicks || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold ${(ad.ctr || 0) > 0.02 ? 'text-green-600' : 'text-gray-500'}`}>
                          {((ad.ctr || 0) * 100).toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ad.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {ad.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Ad Summary Stats */}
          {adPerformance.length > 0 && (
            <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-2xl font-black text-gray-900">
                  {adPerformance.reduce((sum, ad) => sum + (ad.impressions || 0), 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">Total Impressions</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-2xl font-black text-pink-600">
                  {adPerformance.reduce((sum, ad) => sum + (ad.clicks || 0), 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">Total Clicks</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-2xl font-black text-gray-900">
                  {adPerformance.filter(ad => ad.is_active).length}
                </p>
                <p className="text-xs text-gray-500">Active Ads</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="text-2xl font-black text-green-600">
                  {(() => {
                    const totalImpr = adPerformance.reduce((sum, ad) => sum + (ad.impressions || 0), 0);
                    const totalClicks = adPerformance.reduce((sum, ad) => sum + (ad.clicks || 0), 0);
                    return totalImpr > 0 ? ((totalClicks / totalImpr) * 100).toFixed(2) : '0.00';
                  })()}%
                </p>
                <p className="text-xs text-gray-500">Avg CTR</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity Log */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white p-4 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <Clock className="w-5 h-5" />Recent Activity
          </h3>
          <span className="text-sm text-gray-300">Last 20 events</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Event</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Page</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Details</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Device</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {recentEvents.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No events recorded yet
                  </td>
                </tr>
              ) : (
                recentEvents.map((event, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(event.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        event.event_type === 'page_view' ? 'bg-blue-100 text-blue-700' :
                        event.event_type === 'search' ? 'bg-orange-100 text-orange-700' :
                        event.event_type === 'directory_view' ? 'bg-green-100 text-green-700' :
                        event.event_type === 'directory_click' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {formatEventType(event.event_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-sm">
                      {getPageName(event.page) || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {event.entity_name || event.metadata?.query || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-400">
                        <DeviceIcon device={event.device_type} />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Directory Views', value: summary?.directory_views || 0, icon: Building2, color: 'bg-indigo-500' },
          { label: 'Directory Clicks', value: summary?.directory_clicks || 0, icon: MousePointer, color: 'bg-teal-500' },
          { label: 'Ad Impressions', value: summary?.ad_impressions || 0, icon: Target, color: 'bg-pink-500' },
          { label: 'Ad CTR', value: summary?.ad_impressions > 0 ? ((summary?.ad_clicks / summary?.ad_impressions) * 100).toFixed(2) + '%' : '0%', icon: Zap, color: 'bg-amber-500' }
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl p-4 shadow-lg flex items-center gap-4">
            <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
              <stat.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</p>
              <p className="text-gray-500 text-xs">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== ADMIN SETTINGS PAGE (ENHANCED) ====================
export const AdminSettingsPageEnhanced = ({ setPage }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const { data } = await appSettings.getAll();
    const settingsObj = {};
    (data || []).forEach(s => {
      settingsObj[s.key] = typeof s.value === 'string' ? s.value.replace(/^"|"$/g, '') : s.value;
    });
    setSettings(settingsObj);
    setLoading(false);
  };

  const handleSave = async (key, value) => {
    setSaving(true);
    const { error } = await appSettings.set(key, JSON.stringify(value));
    if (error) {
      alert('Error saving setting: ' + error.message);
    } else {
      setSettings({ ...settings, [key]: value });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <RefreshCw className="w-12 h-12 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Globe className="w-8 h-8" />App Settings
        </h2>
        <p className="text-gray-300 mt-1">Configure analytics, ads, and app behavior</p>
      </div>

      {/* Google Analytics Settings */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-blue-600 text-white p-4">
          <h3 className="font-bold flex items-center gap-2"><BarChart3 className="w-5 h-5" />Google Analytics 4</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block font-medium text-gray-700 mb-2">GA4 Measurement ID</label>
            <input
              type="text"
              value={settings.ga4_measurement_id || ''}
              onChange={(e) => setSettings({ ...settings, ga4_measurement_id: e.target.value })}
              placeholder="G-XXXXXXXXXX"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
            />
            <p className="text-sm text-gray-500 mt-1">Enter your Google Analytics 4 Measurement ID</p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-700">Enable GA4 Tracking</label>
              <p className="text-sm text-gray-500">Track page views and events in Google Analytics</p>
            </div>
            <button
              onClick={() => handleSave('ga4_enabled', settings.ga4_enabled === 'true' ? 'false' : 'true')}
              className={`relative w-14 h-7 rounded-full transition-colors ${settings.ga4_enabled === 'true' ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${settings.ga4_enabled === 'true' ? 'left-8' : 'left-1'}`} />
            </button>
          </div>
          <button
            onClick={() => handleSave('ga4_measurement_id', settings.ga4_measurement_id)}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save GA4 Settings'}
          </button>
        </div>
      </div>

      {/* Custom Analytics */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-purple-600 text-white p-4">
          <h3 className="font-bold flex items-center gap-2"><PieChart className="w-5 h-5" />Custom Analytics</h3>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-700">Enable Custom Analytics</label>
              <p className="text-sm text-gray-500">Track events in Supabase for detailed reporting</p>
            </div>
            <button
              onClick={() => handleSave('custom_analytics_enabled', settings.custom_analytics_enabled === 'true' ? 'false' : 'true')}
              className={`relative w-14 h-7 rounded-full transition-colors ${settings.custom_analytics_enabled === 'true' ? 'bg-purple-600' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${settings.custom_analytics_enabled === 'true' ? 'left-8' : 'left-1'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Ads Settings */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-amber-500 text-white p-4">
          <h3 className="font-bold flex items-center gap-2"><Target className="w-5 h-5" />Banner Advertisements</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-700">Enable Banner Ads</label>
              <p className="text-sm text-gray-500">Show banner advertisements on pages</p>
            </div>
            <button
              onClick={() => handleSave('ads_enabled', settings.ads_enabled === 'true' ? 'false' : 'true')}
              className={`relative w-14 h-7 rounded-full transition-colors ${settings.ads_enabled === 'true' ? 'bg-amber-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${settings.ads_enabled === 'true' ? 'left-8' : 'left-1'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Environment Variables */}
      <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
        <h4 className="font-bold text-gray-900 mb-4">Environment Variables</h4>
        <p className="text-gray-600 mb-4 text-sm">
          Some settings are configured via environment variables in your hosting platform:
        </p>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2">
            <code className="bg-gray-200 px-2 py-1 rounded">REACT_APP_SUPABASE_URL</code>
          </li>
          <li className="flex items-center gap-2">
            <code className="bg-gray-200 px-2 py-1 rounded">REACT_APP_SUPABASE_ANON_KEY</code>
          </li>
          <li className="flex items-center gap-2">
            <code className="bg-gray-200 px-2 py-1 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code>
          </li>
        </ul>
      </div>

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

export default AdminAnalyticsPage;
